export enum UserRole {
  ADMIN = 'admin',
  CUSTOMER = 'customer',
  PROVIDER = 'provider' // Use existing provider role from database
}

export interface Role {
  id: number;
  name: string;
}

// Provider-specific data interface
export interface ProviderData {
  providerId: number;
  assignedBrands: Array<{
    id: number;
    name: string;
  }>;
  companyName?: string;
}

export class User {
  constructor (
    public readonly id: number,
    public readonly username: string,
    public readonly email: string,
    public readonly role: Role,
    public readonly status: string,
    public readonly permissions: string[] = [], // Permissions from backend
    public readonly providerData?: ProviderData // Add provider data
  ) {}

  public isAdmin (): boolean {
    return this.role.name === UserRole.ADMIN;
  }

  public isServiceProvider (): boolean {
    // Check if user is a provider based on role or username pattern
    const roleName = this.role?.name ? this.role.name.toLowerCase() : '';
    const providerRoleNames = new Set<string>([
      UserRole.PROVIDER,
      'service_provider',
      'service-provider',
      'serviceprovider'
    ]);

    const isProviderByRole = roleName ? providerRoleNames.has(roleName) : false;
    const isProviderByUsername = this.username.includes('_provider') || this.username === 'zte_provider';

    return isProviderByRole || isProviderByUsername;
  }

  public isActive (): boolean {
    return this.status === 'active';
  }

  public getAssignedBrandIds (): number[] {
    return this.providerData?.assignedBrands?.map(brand => brand.id) || [];
  }

  public getAssignedBrandNames (): string[] {
    return this.providerData?.assignedBrands?.map(brand => brand.name) || [];
  }

  /**
   * Check if user has a specific permission
   */
  public hasPermission (permission: string): boolean {
    return this.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  public hasAnyPermission (permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  public hasAllPermissions (permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }
}
